import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { ROUTES } from "@/config/constants";
import { Loading } from "./ui/Spinner";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  requireAdmin = false,
}) => {
  const { user, loading } = useAuthStore();
  const location = useLocation();
  const [authGateExpired, setAuthGateExpired] = useState(false);

  // Store current location for post-login redirect
  useEffect(() => {
    if (requireAuth && !user && !loading) {
      const currentPath = location.pathname + location.search;
      if (!currentPath.includes("/login") && !currentPath.includes("/signup")) {
        sessionStorage.setItem("wihmedia-return-url", currentPath);
      }
    }
  }, [user, loading, requireAuth, location]);

  useEffect(() => {
    if (!(loading && requireAuth && !user)) {
      setAuthGateExpired(false);
      return;
    }

    // Avoid aggressive redirects during auth hydration/refresh.
    // Local dev + emulator + slow networks can exceed a few seconds.
    const timer = window.setTimeout(() => setAuthGateExpired(true), 60000);
    return () => window.clearTimeout(timer);
  }, [loading, requireAuth, user]);

  // `authStore.loading` is also (mis)used by some pages as a global spinner.
  // For routing, only treat it as an auth-initialization gate when we don't
  // yet know whether a user exists.
  if (loading && requireAuth && !user && !authGateExpired) {
    return <Loading fullScreen text="Loading..." />;
  }

  if (loading && requireAuth && !user && authGateExpired) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  if (requireAuth && !user) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  if (requireAdmin && user?.role !== "admin") {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
